// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for announcement management
import {
  useGetAllAnnouncementsQuery,
  useGetAllActiveAnnouncementQuery,
  useGetAnnouncementByIdQuery,
  useGetAnnouncementByCategoryQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useDeleteAnnouncementMutation,
  useRestoreAnnouncementMutation,
} from '../../../redux/slices/announcement/slice';

export const useGetAllAnnouncements = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllAnnouncements,
      isLoading: isGetAllAnnouncementsLoading,
      isError: isGetAllAnnouncementsError,
      error: getAllAnnouncementsError,
      isFetching: isGetAllAnnouncementsFetching,
      refetch: refetchGetAllAnnouncements,
    } = useGetAllAnnouncementsQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllAnnouncements = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAllAnnouncements !== undefined) {
        setData({
          data: getAllAnnouncements.data?.data,
          total: getAllAnnouncements.data?.total,
        });
        setSkipApi(true);
      }
    }, [getAllAnnouncements]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllAnnouncementsLoading,
        isGetAllAnnouncementsError,
        getAllAnnouncementsError,
        isGetAllAnnouncementsFetching,
        refetchGetAllAnnouncements,
        getAllAnnouncements: handleGetAllAnnouncements,
      }),
      [
        data,
        isGetAllAnnouncementsLoading,
        isGetAllAnnouncementsError,
        getAllAnnouncementsError,
        isGetAllAnnouncementsFetching,
        refetchGetAllAnnouncements,
        handleGetAllAnnouncements,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all announcements:', error);
    throw error;
  }
};

export const useGetAllActiveAnnouncement = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllActiveAnnouncements,
      isLoading: isGetAllActiveAnnouncementLoading,
      isError: isGetAllActiveAnnouncementError,
      error: getAllActiveAnnouncementError,
      isFetching: isGetAllActiveAnnouncementFetching,
      refetch: refetchGetAllActiveAnnouncement,
    } = useGetAllActiveAnnouncementQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllActiveAnnouncement = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAllActiveAnnouncements !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAllActiveAnnouncements.data?.data,
          total: getAllActiveAnnouncements.data?.total,
        });
        setSkipApi(true);
      }
    }, [getAllActiveAnnouncements]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllActiveAnnouncementLoading,
        isGetAllActiveAnnouncementError,
        getAllActiveAnnouncementError,
        isGetAllActiveAnnouncementFetching,
        refetchGetAllActiveAnnouncement,
        getAllActiveAnnouncement: handleGetAllActiveAnnouncement,
      }),
      [
        data,
        isGetAllActiveAnnouncementLoading,
        isGetAllActiveAnnouncementError,
        getAllActiveAnnouncementError,
        isGetAllActiveAnnouncementFetching,
        refetchGetAllActiveAnnouncement,
        handleGetAllActiveAnnouncement,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all active announcements:', error);
    throw error;
  }
};

export const useGetAnnouncementById = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [id, setId] = useState('');

    // api call query
    const {
      data: getAnnouncementById,
      isLoading: isGetAnnouncementByIdLoading,
      isError: isGetAnnouncementByIdError,
      error: getAnnouncementByIdError,
      refetch: refetchGetAnnouncementById,
    } = useGetAnnouncementByIdQuery(id, { skip: skipApi });

    // handlers
    const handleGetAnnouncementById = async id => {
      setSkipApi(false);
      setId(id);
    };

    // effect
    useEffect(() => {
      if (getAnnouncementById !== undefined) {
        setData(getAnnouncementById.data);
        setSkipApi(true);
      }
    }, [getAnnouncementById]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAnnouncementByIdLoading,
        isGetAnnouncementByIdError,
        getAnnouncementByIdError,
        refetchGetAnnouncementById,
        getAnnouncementById: handleGetAnnouncementById,
      }),
      [
        data,
        isGetAnnouncementByIdLoading,
        isGetAnnouncementByIdError,
        getAnnouncementByIdError,
        refetchGetAnnouncementById,
        handleGetAnnouncementById,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting announcement by ID:', error);
    throw error;
  }
};

export const useGetAnnouncementByCategory = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAnnouncementByCategory,
      isLoading: isGetAnnouncementByCategoryLoading,
      isError: isGetAnnouncementByCategoryError,
      error: getAnnouncementByCategoryError,
      isFetching: isGetAnnouncementByCategoryFetching,
      refetch: refetchGetAnnouncementByCategory,
    } = useGetAnnouncementByCategoryQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAnnouncementByCategory = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAnnouncementByCategory !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAnnouncementByCategory.data?.data,
          total: getAnnouncementByCategory.data?.total,
        });
      }
    }, [getAnnouncementByCategory]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAnnouncementByCategoryLoading,
        isGetAnnouncementByCategoryError,
        getAnnouncementByCategoryError,
        isGetAnnouncementByCategoryFetching,
        refetchGetAnnouncementByCategory,
        getAnnouncementByCategory: handleGetAnnouncementByCategory,
      }),
      [
        data,
        isGetAnnouncementByCategoryLoading,
        isGetAnnouncementByCategoryError,
        getAnnouncementByCategoryError,
        isGetAnnouncementByCategoryFetching,
        refetchGetAnnouncementByCategory,
        handleGetAnnouncementByCategory,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting announcement by category:', error);
    throw error;
  }
};

export const useCreateAnnouncement = () => {
  try {
    // api call mutation
    const [
      createAnnouncement,
      {
        isLoading: isCreateAnnouncementLoading,
        isError: isCreateAnnouncementError,
        error: createAnnouncementError,
      },
    ] = useCreateAnnouncementMutation();

    // handlers
    const handleCreateAnnouncement = async data => {
      return await createAnnouncement(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isCreateAnnouncementLoading,
        isCreateAnnouncementError,
        createAnnouncementError,
        createAnnouncement: handleCreateAnnouncement,
      }),
      [
        isCreateAnnouncementLoading,
        isCreateAnnouncementError,
        createAnnouncementError,
        handleCreateAnnouncement,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }
};

export const useUpdateAnnouncement = () => {
  try {
    // api call mutation
    const [
      updateAnnouncement,
      {
        isLoading: isUpdateAnnouncementLoading,
        isError: isUpdateAnnouncementError,
        error: updateAnnouncementError,
      },
    ] = useUpdateAnnouncementMutation();

    // handlers
    const handleUpdateAnnouncement = async ({ id, data }) => {
      return await updateAnnouncement({ id, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateAnnouncementLoading,
        isUpdateAnnouncementError,
        updateAnnouncementError,
        updateAnnouncement: handleUpdateAnnouncement,
      }),
      [
        isUpdateAnnouncementLoading,
        isUpdateAnnouncementError,
        updateAnnouncementError,
        handleUpdateAnnouncement,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating announcement:', error);
    throw error;
  }
};

export const useDeleteAnnouncement = () => {
  try {
    // api call mutation
    const [
      deleteAnnouncement,
      {
        isLoading: isDeleteAnnouncementLoading,
        isError: isDeleteAnnouncementError,
        error: deleteAnnouncementError,
      },
    ] = useDeleteAnnouncementMutation();

    // handlers
    const handleDeleteAnnouncement = async id => {
      return await deleteAnnouncement({ id });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteAnnouncementLoading,
        isDeleteAnnouncementError,
        deleteAnnouncementError,
        deleteAnnouncement: handleDeleteAnnouncement,
      }),
      [
        isDeleteAnnouncementLoading,
        isDeleteAnnouncementError,
        deleteAnnouncementError,
        handleDeleteAnnouncement,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting announcement:', error);
    throw error;
  }
};

export const useRestoreAnnouncement = () => {
  try {
    // api call mutation
    const [
      restoreAnnouncement,
      {
        isLoading: isRestoreAnnouncementLoading,
        isError: isRestoreAnnouncementError,
        error: restoreAnnouncementError,
      },
    ] = useRestoreAnnouncementMutation();

    // handlers
    const handleRestoreAnnouncement = async id => {
      return await restoreAnnouncement({ id });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isRestoreAnnouncementLoading,
        isRestoreAnnouncementError,
        restoreAnnouncementError,
        restoreAnnouncement: handleRestoreAnnouncement,
      }),
      [
        isRestoreAnnouncementLoading,
        isRestoreAnnouncementError,
        restoreAnnouncementError,
        handleRestoreAnnouncement,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error restoring announcement:', error);
    throw error;
  }
};
